import { useId, useState } from 'react'

import { formatNumber } from '@/lib/format'
import { barPath } from '@/components/charts/shapes'
import { ChartFrame, type ChartDatum } from '@/components/charts/ChartFrame'

export interface BarChartProps {
  title: string
  subtitle?: string
  data: ChartDatum[]
  /** Rows to show before folding the tail away. */
  maxRows?: number
  categoryHeading?: string
  valueHeading?: string
  onSelect?: (datum: ChartDatum) => void
  className?: string
}

const ROW_HEIGHT = 22
const BAR_THICKNESS = 12 // spec caps bars at 24px; 16 leaves air in the band
const LABEL_WIDTH = 104
const VALUE_WIDTH = 52

/**
 * Horizontal bars for magnitude across nominal categories.
 *
 * One series, therefore one colour for every bar and no legend; a per-bar hue
 * would double-encode the length the bar already shows, and the axis labels
 * carry identity.
 */
export function BarChart({
  title,
  subtitle,
  data,
  maxRows = 10,
  categoryHeading,
  valueHeading,
  onSelect,
  className,
}: BarChartProps) {
  const [hovered, setHovered] = useState<number | null>(null)
  const clipId = useId()

  const rows = data.slice(0, maxRows)
  const max = Math.max(...rows.map((row) => row.value), 1)
  const height = Math.max(rows.length * ROW_HEIGHT, ROW_HEIGHT)
  const trackWidth = 320
  const width = LABEL_WIDTH + trackWidth + VALUE_WIDTH

  if (rows.length === 0) {
    return (
      <ChartFrame title={title} subtitle={subtitle} data={data} className={className}>
        <p className="py-6 text-center text-xs text-ink-muted">No data yet.</p>
      </ChartFrame>
    )
  }

  return (
    <ChartFrame
      title={title}
      subtitle={subtitle}
      data={data}
      categoryHeading={categoryHeading}
      valueHeading={valueHeading}
      className={className}
    >
      <div className="relative">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full"
          style={{ maxHeight: height * 1.6 }}
          role="img"
          aria-label={`${title}. ${rows.length} categories shown; the table below lists every value.`}
        >
          <defs>
            <clipPath id={clipId}>
              <rect x="0" y="0" width={width} height={height} />
            </clipPath>
          </defs>

          {rows.map((row, index) => {
            const y = index * ROW_HEIGHT
            const barY = y + (ROW_HEIGHT - BAR_THICKNESS) / 2
            const end = LABEL_WIDTH + (row.value / max) * trackWidth
            const active = hovered === index

            return (
              <g
                key={row.key}
                onMouseEnter={() => setHovered(index)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => onSelect?.(row)}
                className={onSelect ? 'cursor-pointer' : undefined}
                clipPath={`url(#${clipId})`}
              >
                {/* Hit target spans the whole row, not just the drawn bar. */}
                <rect
                  x="0"
                  y={y}
                  width={width}
                  height={ROW_HEIGHT}
                  fill={active ? 'var(--app-surface-hover)' : 'transparent'}
                  rx="4"
                />
                <text
                  x={LABEL_WIDTH - 8}
                  y={y + ROW_HEIGHT / 2}
                  textAnchor="end"
                  dominantBaseline="central"
                  className="fill-ink-secondary text-[12px]"
                >
                  {row.label.length > 15 ? `${row.label.slice(0, 14)}…` : row.label}
                </text>

                {/* Track shows the share of the largest value at a glance. */}
                <rect
                  x={LABEL_WIDTH}
                  y={barY}
                  width={trackWidth}
                  height={BAR_THICKNESS}
                  rx="4"
                  className="fill-chart-track"
                />
                <path
                  d={barPath(LABEL_WIDTH, barY, end, BAR_THICKNESS)}
                  className="fill-series-1"
                  opacity={active ? 1 : 0.9}
                />
                <text
                  x={LABEL_WIDTH + trackWidth + 8}
                  y={y + ROW_HEIGHT / 2}
                  dominantBaseline="central"
                  className="fill-ink text-[12px] font-medium tabular-nums"
                >
                  {formatNumber(row.value)}
                </text>
              </g>
            )
          })}
        </svg>

        {data.length > maxRows ? (
          <p className="mt-1.5 text-xs text-ink-muted">
            Showing the top {maxRows} of {data.length}. The table lists them all.
          </p>
        ) : null}
      </div>
    </ChartFrame>
  )
}
